import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { LocationResponse } from "@/types/location";

// Hook for fetching locations for a specific organization
// API returns complete list - filtering/sorting done client-side
export function useLocations(organizationId: string | null) {
  const {
    data: locations = [],
    isLoading,
    error
  } = useQuery<LocationResponse[]>({
    queryKey: ["/organizations", organizationId, "locations"],
    queryFn: async () => {
      if (!organizationId) throw new Error("Organization ID is required");
      const response = await apiRequest("GET", `/organizations/${organizationId}/locations`);
      return response.data || response;
    },
    enabled: !!organizationId
  });

  return {
    locations,
    isLoading,
    error
  };
}

// Hook for fetching a single location
export function useLocation(organizationId: string | null, locationId: string | null) {
  const {
    data: location,
    isLoading,
    error
  } = useQuery<LocationResponse>({
    queryKey: ["/organizations", organizationId, "locations", locationId],
    queryFn: async () => {
      if (!organizationId || !locationId) throw new Error("Organization ID and Location ID are required");
      const response = await apiRequest("GET", `/organizations/${organizationId}/locations/${locationId}`);
      return response.data || response;
    },
    enabled: !!organizationId && !!locationId
  });

  return {
    location,
    isLoading,
    error
  };
}